import React, { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { Mail, Lock, Eye, EyeOff, ArrowRight, Sparkles, Shield, Brain, Star, Zap } from 'lucide-react';
import toast from 'react-hot-toast';
 
const LoginPage = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const { login, isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const { scrollY } = useScroll();
 
  // Parallax effects
  const bgY = useTransform(scrollY, [0, 500], [0, -150]);
  const formY = useTransform(scrollY, [0, 300], [0, -50]);
 
  useEffect(() => {
    let active = true;
 
    const redirectToKeycloak = async () => {
      try {
        await login();
      } catch (error) {
        if (!active) return;
        console.error('Keycloak login redirect failed:', error);
        toast.error(error.message || 'Unable to open Keycloak login.');
      }
    };
 
    redirectToKeycloak();
 
    return () => {
      active = false;
    };
  }, [login]);
 
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
 
 
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
   
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };
 
 
  const handleSubmit = async (e) => {
  e.preventDefault();
 
  try {
    console.log('🔐 LoginPage: Submitting login form');
    await login(formData.email, formData.password);
   
    toast.success('Welcome back! 🚀');
    navigate('/dashboard');
  } catch (error) {
    console.error('❌ LoginPage: Login failed:', error);
    toast.error(error.message || 'Login failed. Please try again.');
  }
};
 
 
  // Floating background elements
  const FloatingElement = ({ children, className, delay = 0 }) => (
    <motion.div
      className={`absolute ${className}`}
      animate={{
        y: [-20, 20, -20],
        rotate: [-5, 5, -5],
        opacity: [0.1, 0.3, 0.1]
      }}
      transition={{
        duration: 8,
        repeat: Infinity,
        ease: "easeInOut",
        delay: delay
      }}
    >
      {children}
    </motion.div>
  );
 
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 relative overflow-hidden">
      {/* Animated background */}
      <motion.div
        className="absolute inset-0 opacity-20"
        style={{ y: bgY }}
      >
        <FloatingElement className="top-20 left-10 text-purple-400" delay={0}>
          <Brain size={40} />
        </FloatingElement>
        <FloatingElement className="top-40 right-20 text-blue-400" delay={1}>
          <Zap size={35} />
        </FloatingElement>
        <FloatingElement className="bottom-40 left-20 text-pink-400" delay={2}>
          <Shield size={45} />
        </FloatingElement>
        <FloatingElement className="bottom-20 right-10 text-green-400" delay={3}>
          <Star size={38} />
        </FloatingElement>
      </motion.div>
 
      {/* Glowing orbs */}
      <motion.div
        className="absolute top-1/4 left-1/4 w-32 h-32 bg-purple-500 rounded-full blur-xl opacity-30"
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.3, 0.6, 0.3]
        }}
        transition={{
          duration: 4,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
      <motion.div
        className="absolute bottom-1/4 right-1/4 w-24 h-24 bg-blue-500 rounded-full blur-xl opacity-30"
        animate={{
          scale: [1, 1.3, 1],
          opacity: [0.3, 0.7, 0.3]
        }}
        transition={{
          duration: 5,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 2
        }}
      />
 
      <div className="relative z-10 min-h-screen flex items-center justify-center px-4 py-12">
        <motion.div
          className="w-full max-w-md"
          style={{ y: formY }}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6 }}
        >
          {/* Header */}
          <motion.div
            className="text-center mb-8"
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <motion.div
              className="flex items-center justify-center mb-4"
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <Sparkles className="w-8 h-8 text-yellow-400 mr-2" />
              <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent font-bold text-3xl">
                AIVIONLearn
              </span>
              <Sparkles className="w-8 h-8 text-yellow-400 ml-2" />
            </motion.div>
           
            <h1 className="text-4xl font-bold text-white mb-2">
              Welcome Back
            </h1>
            <p className="text-gray-300 text-lg">
              Sign in to continue your <span className="text-purple-400">AI-powered</span> journey
            </p>
          </motion.div>
 
          {/* Form Container */}
          <motion.div
            className="bg-slate-800/30 backdrop-blur-xl rounded-2xl p-8 border border-slate-700/50 shadow-2xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            whileHover={{
              scale: 1.02,
              boxShadow: "0 25px 50px -12px rgba(139, 92, 246, 0.25)"
            }}
          >
            {/* Demo Credentials */}
            {/* <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3 }}
              className="bg-purple-600/10 border border-purple-500/20 rounded-xl p-4 mb-8 backdrop-blur-sm"
            >
              <div className="flex items-center mb-2">
                <Shield className="w-4 h-4 text-purple-400 mr-2" />
                <p className="text-purple-400 text-sm font-medium">Demo Credentials:</p>
              </div>
              <div className="space-y-1">
                <p className="text-purple-300 text-sm">
                  <span className="font-mono bg-slate-700/50 px-2 py-1 rounded">test@example.com</span>
                </p>
                <p className="text-purple-300 text-sm">
                  <span className="font-mono bg-slate-700/50 px-2 py-1 rounded">password</span>
                </p>
              </div>
            </motion.div> */}
 
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Email Field */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 }}
              >
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400" />
                  </div>
                  <motion.input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`w-full pl-10 pr-4 py-3 bg-slate-700/50 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 transition-all duration-300 ${
                      errors.email
                        ? 'border-red-500 focus:ring-red-500/50'
                        : 'border-slate-600 focus:ring-purple-500/50 focus:border-purple-500'
                    }`}
                    placeholder="Enter your email"
                    whileFocus={{ scale: 1.02 }}
                    transition={{ duration: 0.2 }}
                  />
                </div>
                {errors.email && (
                  <motion.p
                    className="text-red-400 text-sm mt-1"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    {errors.email}
                  </motion.p>
                )}
              </motion.div>
 
              {/* Password Field */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 }}
              >
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <motion.input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className={`w-full pl-10 pr-12 py-3 bg-slate-700/50 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 transition-all duration-300 ${
                      errors.password
                        ? 'border-red-500 focus:ring-red-500/50'
                        : 'border-slate-600 focus:ring-purple-500/50 focus:border-purple-500'
                    }`}
                    placeholder="Enter your password"
                    whileFocus={{ scale: 1.02 }}
                    transition={{ duration: 0.2 }}
                  />
                  <motion.button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-white transition-colors"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </motion.button>
                </div>
                {errors.password && (
                  <motion.p
                    className="text-red-400 text-sm mt-1"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    {errors.password}
                  </motion.p>
                )}
              </motion.div>
 
              {/* Submit Button */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
              >
                <motion.button
                  type="submit"
                  disabled={isLoading}
                  className="group relative w-full py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold rounded-lg overflow-hidden transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  whileHover={{
                    scale: 1.02,
                    boxShadow: "0 20px 25px -5px rgba(139, 92, 246, 0.4)"
                  }}
                  whileTap={{ scale: 0.98 }}
                >
                  <span className="relative z-10 flex items-center justify-center">
                    {isLoading ? (
                      <>
                        <motion.div
                          className="w-5 h-5 border-2 border-white border-t-transparent rounded-full mr-2"
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                        />
                        Signing In...
                      </>
                    ) : (
                      <>
                        Open Keycloak Login
                        <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                      </>
                    )}
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-r from-purple-700 to-pink-700 transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-300" />
                </motion.button>
              </motion.div>
            </form>
 
            {/* Sign Up Link */}
            <motion.div
              className="text-center mt-8 pt-6 border-t border-slate-700"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7 }}
            >
              <p className="text-gray-400">
                Don't have an account?{' '}
                <Link
                  to="/signup"
                  className="text-purple-400 hover:text-purple-300 font-medium transition-colors"
                >
                  Sign up for free
                </Link>
              </p>
            </motion.div>
          </motion.div>
 
          {/* Features Preview */}
          <motion.div
            className="mt-8"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
          >
            <div className="bg-slate-800/20 backdrop-blur-sm rounded-xl p-6 border border-slate-700/30">
              <h3 className="text-white font-medium mb-4 text-center">Why Choose AIVIONLearn?</h3>
              <div className="grid grid-cols-1 gap-3">
                {[
                  { icon: Brain, text: 'AI-powered personalized learning' },
                  { icon: Zap, text: 'Interactive coding challenges' },
                  { icon: Star, text: 'Industry-recognized certificates' },
                  { icon: Shield, text: 'Progress tracking & analytics' }
                ].map((feature, index) => (
                  <motion.div
                    key={index}
                    className="flex items-center text-gray-300"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.9 + index * 0.1 }}
                  >
                    <feature.icon className="w-4 h-4 text-purple-400 mr-3 flex-shrink-0" />
                    <span className="text-sm">{feature.text}</span>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
 
          {/* Back to Home Link */}
          <motion.div
            className="text-center mt-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1 }}
          >
            <Link
              to="/"
              className="text-gray-400 hover:text-purple-400 text-sm transition-colors"
            >
              ← Back to Home
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};
 
export default LoginPage;
 
 
 